import { Controller } from "@hotwired/stimulus"
import tippy from 'tippy.js'

export default class extends Controller {
  static values = { 
    url: String,
    debounce: { type: Number, default: 800 },
    autofocus: { type: Boolean, default: false }
  }
  static targets = ["field"]
  
  connect() {
    this.timeout = null
    this.tippyInstances = new Map()
    this.pendingSaves = new Map()
    this.shiftPressed = false
    
    // Track Shift key state globally
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleKeyUp = this.handleKeyUp.bind(this)
    document.addEventListener('keydown', this.handleKeyDown)
    document.addEventListener('keyup', this.handleKeyUp)
    
    // Auto-focus first field if autofocus value is true
    if (this.autofocusValue && this.hasFieldTarget) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.fieldTargets[0]?.focus()
      })
    }
  }
  
  disconnect() {
    clearTimeout(this.timeout)
    this.tippyInstances.forEach(instance => instance.destroy())
    this.tippyInstances.clear()
    document.removeEventListener('keydown', this.handleKeyDown)
    document.removeEventListener('keyup', this.handleKeyUp)
  }
  
  handleKeyDown(event) {
    if (event.key === 'Shift') {
      this.shiftPressed = true
    }
  }
  
  handleKeyUp(event) {
    if (event.key === 'Shift') {
      this.shiftPressed = false
    }
  }
  
  fieldChanged(event) {
    const field = event.target
    const fieldName = field.name
    
    // Check for Shift+change on select elements for bulk update
    if (this.shiftPressed && field.tagName === 'SELECT') {
      this.bulkUpdate(field)
      return
    }
    
    // Clear any pending save for this field
    if (this.pendingSaves.has(fieldName)) {
      clearTimeout(this.pendingSaves.get(fieldName))
    }
    
    // Schedule new save with debounce
    const timeoutId = setTimeout(() => {
      this.save(field)
      this.pendingSaves.delete(fieldName)
    }, this.debounceValue)
    
    this.pendingSaves.set(fieldName, timeoutId)
  }
  
  async bulkUpdate(field) {
    const fieldName = field.name
    const fieldValue = field.value
    
    // Find the tenants table
    const table = document.getElementById('tenants_table')
    if (!table) {
      console.error('Tenants table not found')
      return
    }
    
    const bulkUpdateUrl = table.dataset.bulkUpdateUrl
    if (!bulkUpdateUrl) {
      console.error('Bulk update URL not found')
      return
    }
    
    // Get all tenant IDs from table rows
    const tenantIds = Array.from(table.querySelectorAll('tbody tr[id^="tenant_"]'))
      .map(row => row.id.replace('tenant_', ''))
    
    if (tenantIds.length === 0) {
      return
    }
    
    // Show saving state on all dropdowns in the same column
    const allDropdowns = table.querySelectorAll(`tbody select[name="${fieldName}"]`)
    allDropdowns.forEach(dropdown => {
      dropdown.classList.add('is-saving')
    })
    
    try {
      const formData = new FormData()
      formData.append('tenant_ids', tenantIds.join(','))
      formData.append('bulk_update[field]', fieldName)
      formData.append('bulk_update[value]', fieldValue)
      
      const response = await fetch(bulkUpdateUrl, {
        method: 'PATCH',
        headers: { 
          'Accept': 'text/vnd.turbo-stream.html', 
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content 
        },
        body: formData
      })
      
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        console.error('Bulk update failed')
      }
    } catch (error) {
      console.error('Bulk update error:', error)
    }
  }
  
  async save(field) {
    const fieldName = field.name
    const fieldValue = field.type === 'checkbox' ? field.checked : field.value
    
    // Save cursor position before request
    const selectionStart = field.selectionStart
    const selectionEnd = field.selectionEnd
    const wasActive = document.activeElement === field
    
    // Add saving indicator
    field.classList.add('is-saving')
    
    try {
      const formData = new FormData()
      formData.append(`update[${fieldName}]`, fieldValue)
      
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: { 
          'Accept': 'application/json', 
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content 
        },
        body: formData
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.clearError(field)
        this.updateFilledStatus(result.filled)
        this.updateAppliedLeaseEndDate(result.applied_lease_end_date)
        this.updateAnnualRent(result.annual_rent)
      } else {
        const errorMessage = result.errors?.[fieldName]?.join(', ') || 'Validation error'
        this.showError(field, errorMessage)
      }
    } catch (error) {
      console.error('Save error:', error)
      this.showError(field, 'Network error. Please try again.')
    } finally {
      field.classList.remove('is-saving')
      
      // Restore cursor position if field is still focused
      if (wasActive && document.activeElement === field && field.setSelectionRange) {
        try {
          field.setSelectionRange(selectionStart, selectionEnd)
        } catch (e) {
          // setSelectionRange may not work for all input types (e.g., date, number)
        }
      }
    }
  }
  
  showError(field, message) {
    field.classList.remove('is-valid')
    field.classList.add('is-invalid')
    
    // Destroy existing tippy if any
    if (this.tippyInstances.has(field)) {
      this.tippyInstances.get(field).destroy()
    }
    
    // Create new tippy with error message
    const instance = tippy(field, {
      content: message,
      theme: 'error',
      placement: 'top',
      trigger: 'manual',
      hideOnClick: false,
      arrow: true
    })
    instance.show()
    
    this.tippyInstances.set(field, instance)
  }
  
  clearError(field) {
    field.classList.remove('is-invalid')
    
    // Destroy tippy if exists
    if (this.tippyInstances.has(field)) {
      this.tippyInstances.get(field).destroy()
      this.tippyInstances.delete(field)
    }
  }
  
  updateFilledStatus(filled) {
    // Find the status column in this row
    const statusCell = this.element.querySelector('.tenant-status-col')
    if (!statusCell) return
    
    const warningIcon = statusCell.querySelector('i')
    
    if (filled && warningIcon) {
      // Remove warning icon when all required fields are filled
      warningIcon.remove()
    } else if (!filled && !warningIcon) {
      // Add warning icon when required fields are missing
      const icon = document.createElement('i')
      icon.className = 'bi bi-exclamation-triangle-fill text-warning'
      icon.title = 'Required fields are not filled'
      statusCell.appendChild(icon)
    }
  }

  updateAppliedLeaseEndDate(value) {
    if (!value) return
    
    // Find the applied_end cell by ID pattern (applied_end_tenant_XXX)
    // Rails dom_id(tenant, 'applied_end') generates "applied_end_tenant_123"
    const rowId = this.element.id  // e.g., "tenant_123"
    const cellId = 'applied_end_' + rowId
    const appliedEndCell = document.getElementById(cellId)
    
    if (appliedEndCell) {
      appliedEndCell.textContent = value
    }
  }

  updateAnnualRent(value) {
    if (value === undefined || value === null) return
    
    // Find the annual_rent cell by ID pattern (annual_rent_tenant_XXX)
    const rowId = this.element.id
    const cellId = 'annual_rent_' + rowId
    const annualRentCell = document.getElementById(cellId)
    
    if (annualRentCell) {
      annualRentCell.textContent = value
    }
  }
}
